// src/components/circle/circle.component.js
import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    min-height: 100%;
    width: 100%;
  }

  body {
    overflow-x: hidden;
    overflow-y: auto;
    background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
    font-family: Arial, sans-serif;
  }

  .p-circle {
    word-break: break-word;
    overflow-wrap: break-word;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  overflow-x: hidden;
  overflow-y: auto;
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: 800px;
  padding: 20px 0 40px 0;
  box-sizing: border-box;
`;

const CircleCard = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 20px;
  margin: 20px;
  width: calc(100% - 40px);
  max-width: 600px;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
`;

const CircleTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: center;
`;

const CircleDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const JoinButton = styled.button`
  background-color: #007df4;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  margin-top: 6px;

  &:hover {
    background-color: #0068cc;
  }
`;

class Circle extends Component {
  constructor(props) {
    super(props);

    const circleId = this.props.match.params.id;

    this.state = {
      circle: {
        id: circleId || "default",
        title: "Fitness Circle",
        description:
          "Join our fitness circle to connect with like-minded people and achieve your goals together.",
        imageUrl: "https://www.bealyo.com/images/prebealyoshare.png",
        createdDate: "2025-01-01",
        participants: 1245,
      },
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    const circleId = this.props.match.params.id;

    console.log("Circle ID:", circleId);

    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    axios
      .post(process.env.REACT_APP_API_URL + "circles/pub/byid/", {
        circleId: circleId,
      })
      .then((response) => {
        const apiData = response.data;
        const circleData = apiData.circle;

        console.log("Circle Data:", circleData);

        const randomPlaceholder = Math.floor(Math.random() * 3) + 1;

        this.setState({
          circle: {
            id: circleData._id,
            title: circleData.name,
            description: circleData.description,
            imageUrl:
              circleData.circle_image && circleData.circle_image !== ""
                ? process.env.REACT_APP_IMG_FOLDER + circleData.circle_image
                : process.env.REACT_APP_IMG_FOLDER +
                  `files/circles/placeholder${randomPlaceholder}.png`,
            createdDate: new Date(circleData.created_at).toLocaleDateString(),
            participants: circleData.members ? circleData.members.length : 0,
            type: circleData.circle_type,
          },
          loading: false,
        });
      })
      .catch((error) => {
        console.error("Error fetching circle:", error);
        this.setState({
          error: "Failed to load circle details",
          loading: false,
        });
      });
  }
  handleJoinClick = (chid) => {
    ReactGA.event({
      category: "Circle",
      action: "Join Circle Click",
      label: this.state.circle.title,
    });

    const deepLink = `bealyo-app://circle/${chid}`;
    const playStore =
      "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

    window.location.href = deepLink;

    setTimeout(() => {
      window.location.href = playStore;
    }, 1500);
  };

  render() {
    const { circle } = this.state;
    const shareUrl = `https://www.bealyo.com/circles/${circle.id}`;

    const ogTitle = circle.title || "Join a Bealyo Circle";
    const ogDescription =
      circle.description ||
      "Be part of a community-driven challenge on Bealyo. Android Open Testing is live.";

    return (
      <PageContainer>
        <Helmet>
          <title>{circle.title} | Bealyo</title>
          <meta name="description" content={circle.description} />

          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://circle/${circle.id}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          <meta
            property="al:ios:url"
            content={`bealyo-app://circle/${circle.id}`}
          />
          <meta property="al:ios:app_store_id" content="YOUR_APP_STORE_ID" />
          <meta property="al:ios:app_name" content="Bealyo" />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={circle.imageUrl} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={circle.imageUrl} />
        </Helmet>

        <GlobalStyle />

        <CenteredContainer>
          <CircleCard>
            <CircleTitle>{circle.title}</CircleTitle>

            <div style={{ margin: "10px 0", textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "18px" }}>
                Real people building habits together on Bealyo
              </div>
              <div style={{ fontSize: "13px", opacity: 0.85 }}>
                This circle was created on Bealyo — a community-driven challenge
                app.
              </div>
            </div>

            <div style={{ margin: "15px 0" }}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "230px",
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
                }}
              >
                <img
                  src={circle.imageUrl}
                  alt={circle.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "55%",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "12px",
                    left: "14px",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Join the movement.
                </div>
              </div>
            </div>

            <CircleDescription>{circle.description}</CircleDescription>

            <div
              style={{
                display: "flex",
                gap: "12px",
                fontSize: "13px",
                margin: "10px 0 6px 0",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {circle.createdDate && <span>🗓 {circle.createdDate}</span>}
              {circle.participants ? (
                <span>👥 {circle.participants} members</span>
              ) : null}
              {circle.type && <span>🎯 {circle.type}</span>}
            </div>

            <div
              style={{
                margin: "8px 0 14px 0",
                fontSize: "13px",
                opacity: 0.92,
              }}
            >
              Join a supportive community on Bealyo
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <span
                style={{
                  alignSelf: "flex-start",
                  background: "#1d9bf0",
                  color: "#fff",
                  borderRadius: "999px",
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: 500,
                  letterSpacing: "0.1px",
                }}
              >
                Early Access
              </span>

              <JoinButton
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                    "_blank",
                  )
                }
                style={{
                  backgroundColor: "#007DF4",
                  marginTop: "2px",
                  padding: "14px 20px",
                  fontSize: "15px",
                  fontWeight: 500,
                }}
              >
                Get Bealyo (Android Open Testing)
              </JoinButton>

              <div style={{ fontSize: "12px", opacity: 0.85 }}>
                Available on Android (Open Testing) · iOS coming later
              </div>

              <button
                onClick={() => this.handleJoinClick(circle.id)}
                style={{
                  backgroundColor: "transparent",
                  color: "#cde9ff",
                  border: "1px solid #1d9bf0",
                  padding: "10px 18px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                View in App
              </button>
            </div>
          </CircleCard>
        </CenteredContainer>
      </PageContainer>
    );
  }
}

export default Circle;
